import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import { BarChart3, Loader2, TrendingUp, TrendingDown, Building2, FileSpreadsheet } from 'lucide-react';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL || '';
const fmt = (n, c = 'PHP') => new Intl.NumberFormat('en-PH', { style: 'currency', currency: c, maximumFractionDigits: 0 }).format(n || 0);

export default function FinanceReportsPage() {
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = today.slice(0, 8) + '01';
  const [start, setStart] = useState(monthStart);
  const [end, setEnd] = useState(today);
  const [pl, setPL] = useState(null);
  const [bs, setBS] = useState(null);
  const [cf, setCF] = useState(null);
  const [arAging, setArAging] = useState(null);
  const [apAging, setApAging] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('pl');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: p }, { data: b }, { data: c }, { data: ar }, { data: ap }] = await Promise.all([
        axios.get(`${API}/api/finance/reports/profit-loss`, { params: { start, end } }),
        axios.get(`${API}/api/finance/reports/balance-sheet`, { params: { as_of: end } }),
        axios.get(`${API}/api/finance/reports/cash-flow`, { params: { start, end } }),
        axios.get(`${API}/api/finance/reports/receivables-aging`),
        axios.get(`${API}/api/finance/reports/payables-aging`),
      ]);
      setPL(p); setBS(b); setCF(c); setArAging(ar); setApAging(ap);
    } catch {
      toast.error('Failed to load reports');
    } finally { setLoading(false); }
  }, [start, end]);

  useEffect(() => { load(); }, [load]);

  const exportCSV = () => {
    if (!pl) return;
    const rows = [
      ['Profit & Loss', `${start} to ${end}`],
      ['Revenue', pl.revenue],
      ['Expenses', pl.total_expenses],
      ['Net income', pl.net_income],
      ['Profit margin', (pl.profit_margin * 100).toFixed(2) + '%'],
      [],
      ['Expense breakdown'],
      ['Account', 'Amount'],
      ...pl.expense_breakdown.map(b => [b.name, b.amount]),
    ];
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `profit-loss-${start}-${end}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success('Exported');
  };

  return (
    <div className="page-shell-wide" data-testid="finance-reports-page">
      <div className="flex items-end justify-between gap-3 mb-5 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-gray-400 mb-1">
            <BarChart3 className="h-3 w-3" /> Finance · Reports
          </div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Financial Reports<PageInfoTooltip pageKey="finance-reports" /></h1>
          <p className="text-sm text-gray-600 mt-1">P&amp;L, balance sheet, cash flow, and aging — accountant-ready exports.</p>
        </div>
        <div className="flex items-end gap-2">
          <div><Label className="text-[10px]">From</Label><Input type="date" value={start} onChange={e => setStart(e.target.value)} className="h-9 w-36" data-testid="report-start" /></div>
          <div><Label className="text-[10px]">To</Label><Input type="date" value={end} onChange={e => setEnd(e.target.value)} className="h-9 w-36" data-testid="report-end" /></div>
          <Button variant="outline" size="sm" onClick={exportCSV} data-testid="export-csv-btn"><FileSpreadsheet className="h-3.5 w-3.5 mr-1" />Export CSV</Button>
        </div>
      </div>

      {loading || !pl ? (
        <div className="p-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>
      ) : (
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="grid grid-cols-4 max-w-2xl mb-4">
            <TabsTrigger value="pl" data-testid="tab-pl">P&amp;L</TabsTrigger>
            <TabsTrigger value="bs" data-testid="tab-bs">Balance sheet</TabsTrigger>
            <TabsTrigger value="cf" data-testid="tab-cf">Cash flow</TabsTrigger>
            <TabsTrigger value="aging" data-testid="tab-aging">Aging</TabsTrigger>
          </TabsList>

          <TabsContent value="pl">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4">
              <Card className="border-emerald-200 bg-emerald-50"><CardContent className="p-4"><div className="text-[10px] uppercase tracking-wider text-emerald-700">Revenue</div><div className="text-2xl font-bold text-emerald-900 mt-1">{fmt(pl.revenue)}</div></CardContent></Card>
              <Card className="border-rose-200 bg-rose-50"><CardContent className="p-4"><div className="text-[10px] uppercase tracking-wider text-rose-700">Expenses</div><div className="text-2xl font-bold text-rose-900 mt-1">{fmt(pl.total_expenses)}</div></CardContent></Card>
              <Card className={pl.net_income >= 0 ? 'border-sky-200 bg-sky-50' : 'border-rose-200 bg-rose-50'}><CardContent className="p-4"><div className="text-[10px] uppercase tracking-wider text-gray-600">Net income · {(pl.profit_margin * 100).toFixed(1)}% margin</div><div className="text-2xl font-bold mt-1">{fmt(pl.net_income)}</div></CardContent></Card>
            </div>
            <Card>
              <CardHeader><CardTitle className="text-base">Expense breakdown</CardTitle></CardHeader>
              <CardContent>
                <table className="w-full text-sm">
                  <tbody>
                    {pl.expense_breakdown.map(b => (
                      <tr key={b.account_code} className="border-b border-gray-100" data-testid={`pl-row-${b.account_code}`}>
                        <td className="py-2 font-mono text-xs text-gray-400 w-16">{b.account_code}</td>
                        <td className="py-2 text-gray-700">{b.name}</td>
                        <td className="py-2 text-right font-mono">{fmt(b.amount)}</td>
                      </tr>
                    ))}
                    {pl.expense_breakdown.length === 0 && (
                      <tr><td className="py-8 text-center text-sm text-gray-400" colSpan={3}>No expenses in this period.</td></tr>
                    )}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="bs">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card><CardHeader><CardTitle className="text-base flex items-center gap-1.5"><TrendingUp className="h-4 w-4 text-emerald-600" />Assets</CardTitle></CardHeader><CardContent className="space-y-2 text-sm">
                <Row label="Cash & bank" value={bs.assets.cash} />
                <Row label="Accounts receivable" value={bs.assets.accounts_receivable} />
                <Row label="Total assets" value={bs.total_assets} bold />
              </CardContent></Card>
              <Card><CardHeader><CardTitle className="text-base flex items-center gap-1.5"><TrendingDown className="h-4 w-4 text-rose-600" />Liabilities</CardTitle></CardHeader><CardContent className="space-y-2 text-sm">
                <Row label="Accounts payable" value={bs.liabilities.accounts_payable} />
                <Row label="Total liabilities" value={bs.total_liabilities} bold />
              </CardContent></Card>
              <Card><CardHeader><CardTitle className="text-base flex items-center gap-1.5"><Building2 className="h-4 w-4 text-sky-600" />Equity</CardTitle></CardHeader><CardContent className="space-y-2 text-sm">
                <Row label="Retained earnings" value={bs.equity} />
                <Row label="Total equity" value={bs.equity} bold />
                <div className="text-[10px] text-gray-400 mt-3">As of {bs.as_of}</div>
              </CardContent></Card>
            </div>
          </TabsContent>

          <TabsContent value="cf">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card><CardHeader><CardTitle className="text-base text-emerald-700">Inflows</CardTitle></CardHeader><CardContent className="space-y-2 text-sm">
                <Row label="Invoice payments received" value={cf.inflows.invoice_payments} />
                <Row label="Total inflows" value={cf.total_inflows} bold />
              </CardContent></Card>
              <Card><CardHeader><CardTitle className="text-base text-rose-700">Outflows</CardTitle></CardHeader><CardContent className="space-y-2 text-sm">
                <Row label="Direct expenses" value={cf.outflows.direct_expenses} />
                <Row label="Bill payments" value={cf.outflows.bill_payments} />
                <Row label="Total outflows" value={cf.total_outflows} bold />
              </CardContent></Card>
            </div>
            <Card className="mt-4 border-2 border-gray-900">
              <CardContent className="p-5 flex items-center justify-between">
                <span className="text-sm font-semibold">Net cash flow ({cf.period.start} → {cf.period.end})</span>
                <span className={`text-2xl font-bold font-mono ${cf.net_cash_flow >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{fmt(cf.net_cash_flow)}</span>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="aging">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <AgingCard title="Accounts Receivable" data={arAging} />
              <AgingCard title="Accounts Payable" data={apAging} />
            </div>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className={`flex justify-between items-center ${bold ? 'pt-2 mt-1 border-t border-gray-200 font-semibold' : ''}`}>
      <span className={bold ? 'text-gray-900' : 'text-gray-600'}>{label}</span>
      <span className="font-mono">{fmt(value)}</span>
    </div>
  );
}

function AgingCard({ title, data }) {
  if (!data) return null;
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">{title}</CardTitle></CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(data.buckets).map(([bucket, amount]) => (
            <div key={bucket} className={`p-3 rounded-md border ${bucket === '90+' && amount > 0 ? 'border-rose-300 bg-rose-50' : 'border-gray-200'}`}>
              <div className="text-[10px] uppercase tracking-wider text-gray-500">{bucket} days</div>
              <div className="text-lg font-bold mt-1 font-mono">{fmt(amount)}</div>
            </div>
          ))}
        </div>
        <div className="mt-3 pt-3 border-t border-gray-100 flex justify-between items-center text-sm">
          <span className="text-gray-600">Total outstanding</span>
          <span className="font-bold font-mono">{fmt(data.total_outstanding)}</span>
        </div>
      </CardContent>
    </Card>
  );
}
